// Public entry point for client analytics. Every property shape is defined by
// the contract, so a wrong shape fails typecheck rather than shipping a bad
// event. track() itself must never throw and must never return a promise —
// nothing here is allowed to slow down or break the page it's called from.
import type { ClientEventMap } from '@suitescanner/analytics-contract';
import { enqueue, flushImmediately } from './queue';

type TrackOptions = {
    // Skips the count/interval thresholds and flushes via sendBeacon right
    // away — for callers on their way out (unload handlers) that cannot rely
    // on a later scheduled flush ever running.
    immediate?: boolean;
};

function isEnabled(): boolean {
    try {
        return process.env.EXPO_PUBLIC_ANALYTICS_ENABLED === 'true' && Boolean(process.env.EXPO_PUBLIC_ANALYTICS_URL);
    } catch {
        return false;
    }
}

function track<N extends keyof ClientEventMap>(name: N, props: ClientEventMap[N], options?: TrackOptions): void {
    try {
        if (!isEnabled()) return;

        enqueue(name, props);

        if (options?.immediate) {
            flushImmediately();
        }
    } catch {
        // A traveller must never see an error or a delay because of analytics.
    }
}

export { track };
